import { useState, useEffect, useCallback } from 'react';
import {
  Search, Filter, X, Loader2, FileCheck, AlertTriangle, CheckCircle2, XCircle,
  Clock, ChevronDown, ChevronUp, Eye, RotateCcw, RefreshCw,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

// ==================== Types ====================
interface EInvoiceLog {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  customerName: string;
  customerGstin: string;
  irn: string | null;
  status: 'GENERATED' | 'PENDING' | 'FAILED' | 'CANCELLED';
  errorMessage: string | null;
  generatedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  invoiceTotal: number | string;
}

interface EInvoiceStats {
  total: number;
  generated: number;
  pending: number;
  failed: number;
  cancelled: number;
}

const STATUS_COLORS: Record<string, string> = {
  GENERATED: 'bg-green-100 text-green-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  FAILED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

const STATUS_OPTIONS = ['GENERATED', 'PENDING', 'FAILED', 'CANCELLED'];

// ==================== Main Component ====================
export function EInvoiceDashboard() {
  const [logs, setLogs] = useState<EInvoiceLog[]>([]);
  const [stats, setStats] = useState<EInvoiceStats>({ total: 0, generated: 0, pending: 0, failed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Action states
  const [generating, setGenerating] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Detail modal
  const [detailLog, setDetailLog] = useState<EInvoiceLog | null>(null);

  // Cancel modal
  const [cancelTarget, setCancelTarget] = useState<EInvoiceLog | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      const res = await api.get('/gst-compliance/einvoice/logs', { params });
      setLogs(res.data.data?.logs ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load e-Invoice logs');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, dateFrom, dateTo, sortField, sortDir, page]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/gst-compliance/einvoice/stats');
      setStats(res.data.data ?? { total: 0, generated: 0, pending: 0, failed: 0, cancelled: 0 });
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  useEffect(() => { setPage(1); }, [search, filterStatus, dateFrom, dateTo]);

  const totalPages = Math.ceil(total / limit);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // Generate IRN
  const handleGenerate = async (invoiceId: string) => {
    setGenerating(invoiceId);
    try {
      await api.post('/gst-compliance/einvoice/generate', { invoiceId });
      fetchLogs();
      fetchStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to generate IRN');
    } finally {
      setGenerating(null);
    }
  };

  // Cancel IRN
  const handleCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setCancelling(cancelTarget.id);
    try {
      await api.post('/gst-compliance/einvoice/cancel', { invoiceId: cancelTarget.invoiceId, reason: cancelReason });
      setCancelTarget(null);
      setCancelReason('');
      fetchLogs();
      fetchStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to cancel IRN');
    } finally {
      setCancelling(null);
    }
  };

  // Bulk generate
  const handleBulkGenerate = async () => {
    const pendingIds = logs.filter(l => l.status === 'PENDING' && selectedIds.has(l.id)).map(l => l.invoiceId);
    if (pendingIds.length === 0) return;
    setBulkGenerating(true);
    try {
      await Promise.all(pendingIds.map(id => api.post('/gst-compliance/einvoice/generate', { invoiceId: id })));
      setSelectedIds(new Set());
      fetchLogs();
      fetchStats();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Some IRN generations failed');
    } finally {
      setBulkGenerating(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pendingLogs = logs.filter(l => l.status === 'PENDING');
    if (pendingLogs.every(l => selectedIds.has(l.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingLogs.map(l => l.id)));
    }
  };

  const canCancelIRN = (log: EInvoiceLog) => {
    if (log.status !== 'GENERATED' || !log.generatedAt) return false;
    const generated = new Date(log.generatedAt);
    const now = new Date();
    return (now.getTime() - generated.getTime()) < 24 * 60 * 60 * 1000;
  };

  const truncateIRN = (irn: string | null) => {
    if (!irn) return '-';
    return irn.length > 20 ? irn.slice(0, 10) + '...' + irn.slice(-10) : irn;
  };

  const pendingSelected = logs.filter(l => l.status === 'PENDING' && selectedIds.has(l.id)).length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">e-Invoice Dashboard</h1>
          <p className="text-sm text-gray-500">Generate and manage e-Invoices (IRN) via WhiteBooks GSP</p>
        </div>
        {pendingSelected > 0 && (
          <button
            onClick={handleBulkGenerate}
            disabled={bulkGenerating}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {bulkGenerating ? <Loader2 size={16} className="animate-spin" /> : <FileCheck size={16} />}
            Generate {pendingSelected} IRN{pendingSelected > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
        <StatCard label="Total" value={stats.total} icon={<FileCheck size={18} />} color="text-gray-900" />
        <StatCard label="Generated" value={stats.generated} icon={<CheckCircle2 size={18} />} color="text-green-600" />
        <StatCard label="Pending" value={stats.pending} icon={<Clock size={18} />} color="text-yellow-600" />
        <StatCard label="Failed" value={stats.failed} icon={<AlertTriangle size={18} />} color="text-red-600" />
        <StatCard label="Cancelled" value={stats.cancelled} icon={<XCircle size={18} />} color="text-gray-500" />
      </div>

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by invoice number, customer..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            placeholder="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
            placeholder="To date"
          />
          {(filterStatus || dateFrom || dateTo) && (
            <button
              onClick={() => { setFilterStatus(''); setDateFrom(''); setDateTo(''); }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading e-Invoice logs...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchLogs} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FileCheck size={40} className="mb-2" />
            <p className="text-sm">No e-Invoice records found</p>
            <p className="text-xs">e-Invoices will appear here once generated from confirmed invoices.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={logs.filter(l => l.status === 'PENDING').length > 0 && logs.filter(l => l.status === 'PENDING').every(l => selectedIds.has(l.id))}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
                    />
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('invoiceNumber')}>
                    <span className="flex items-center gap-1">Invoice # <SortIcon field="invoiceNumber" /></span>
                  </th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">GSTIN</th>
                  <th className="px-4 py-3">IRN</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('createdAt')}>
                    <span className="flex items-center gap-1">Date <SortIcon field="createdAt" /></span>
                  </th>
                  <th className="px-4 py-3 text-center w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      {log.status === 'PENDING' && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(log.id)}
                          onChange={() => toggleSelect(log.id)}
                          className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{log.invoiceNumber}</td>
                    <td className="px-4 py-3 text-gray-600">{log.customerName}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{log.customerGstin || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs" title={log.irn || undefined}>{truncateIRN(log.irn)}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[log.status] || STATUS_COLORS.PENDING}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(log.createdAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {/* View Details */}
                        <button
                          onClick={() => setDetailLog(log)}
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)] transition-colors"
                          title="View Details"
                        >
                          <Eye size={15} />
                        </button>
                        {/* Generate IRN */}
                        {(log.status === 'PENDING' || log.status === 'FAILED') && (
                          <button
                            onClick={() => handleGenerate(log.invoiceId)}
                            disabled={generating === log.invoiceId}
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-green-50 hover:text-green-600 transition-colors disabled:opacity-50"
                            title="Generate IRN"
                          >
                            {generating === log.invoiceId ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                          </button>
                        )}
                        {/* Cancel IRN */}
                        {canCancelIRN(log) && (
                          <button
                            onClick={() => { setCancelTarget(log); setCancelReason(''); }}
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Cancel IRN (within 24h)"
                          >
                            <RotateCcw size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <div className="text-sm text-gray-500">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* ==================== Detail Modal ==================== */}
      {detailLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetailLog(null)}>
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">e-Invoice Details</h3>
              <button onClick={() => setDetailLog(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <DetailRow label="Invoice #" value={detailLog.invoiceNumber} />
              <DetailRow label="Customer" value={detailLog.customerName} />
              <DetailRow label="GSTIN" value={detailLog.customerGstin || '-'} />
              <DetailRow label="Invoice Total" value={formatINR(detailLog.invoiceTotal)} />
              <DetailRow label="Status" value={
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[detailLog.status]}`}>{detailLog.status}</span>
              } />
              <DetailRow label="IRN" value={detailLog.irn || '-'} mono />
              {detailLog.generatedAt && <DetailRow label="Generated At" value={formatDate(detailLog.generatedAt)} />}
              {detailLog.cancelledAt && <DetailRow label="Cancelled At" value={formatDate(detailLog.cancelledAt)} />}
              {detailLog.errorMessage && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <p className="font-medium">Error:</p>
                  <p>{detailLog.errorMessage}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== Cancel Modal ==================== */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCancelTarget(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Cancel e-Invoice</h3>
              <button onClick={() => setCancelTarget(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <p className="mb-3 text-sm text-gray-600">
              Cancel IRN for invoice <strong>{cancelTarget.invoiceNumber}</strong>? This action cannot be undone.
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700">Reason for cancellation *</label>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              placeholder="Enter reason for cancelling this e-Invoice..."
            />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setCancelTarget(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleCancel}
                disabled={!cancelReason.trim() || !!cancelling}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? <Loader2 size={14} className="animate-spin" /> : null}
                Cancel IRN
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Sub-components ====================
function StatCard({ label, value, icon, color }: { label: string; value: number; icon: React.ReactNode; color: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 text-gray-400">{icon}<p className="text-xs text-gray-500">{label}</p></div>
      <p className={`mt-1 text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}

function DetailRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className={`text-right text-gray-900 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</span>
    </div>
  );
}

export default EInvoiceDashboard;
